import { Box, Stack, useTheme } from "@mui/material";
import {
  Button,
  DeleteConfirmationModal,
  PlusIcon,
  SearchInput,
} from "components";
import { HomeScreenLayout } from "components/layout/HomeScreenLayout";
import { DataTable } from "components/table/DataTable";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DrawerComponent } from "./components/DrawerComponent";
import {
  useDeleteProductConfig,
  useGetAllProductConfigs,
  useGlobalStore,
  useMainStore,
} from "controller";
import toast from "react-hot-toast";
import { Decimal } from "utilz";
import {
  formatNumberAsCurrency,
  useMixpanelPageViewTracker,
  usePaginationAndSort,
} from "utilz/helpers";

const BillingHeaders = [
  {
    name: "Name",
    sortable: true,
    key: "product_name",
  },
  {
    name: "Product Type",
    sortable: true,
    key: "product_type",
  },
  {
    name: "Price",
    sortable: true,
    key: "price",
  },
];

export const ServiceDeskBillingProduct = (data: any) => {
  useMixpanelPageViewTracker({ event: "products_page_visited" });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>([]);
  const [mode, setMode] = useState<any>({ method: "create", data: null });
  const theme = useTheme();
  const [paginationAndSort, setPaginationAndSort] = usePaginationAndSort({
    pageNo: 1,
    perPageCount: 50,
    sortColumn: "product_name",
    sortDirection: "desc",
    searchQuery: null,
  });

  const [searchValue, setSearchValue] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productDataResult, setProductDataResult] = useState<[]>([]);
  const currency = useGlobalStore((state) => state.currency);

  const {
    data: productData,
    isFetching: isproductDataLoading,
    refetch,
  } = useGetAllProductConfigs({
    filter: [],
    pageNumber: paginationAndSort.pageNo,
    perPageCount: paginationAndSort.perPageCount,
    sortColumn: paginationAndSort.sortColumn,
    sortDirection: paginationAndSort.sortDirection,
    searchQuery: paginationAndSort.searchQuery || undefined,
  });

  const cellBuilders = {
    price: (rowData: any) =>
      formatNumberAsCurrency({
        value: new Decimal(rowData?.price ?? 0).div(100).toNumber(),
        currency: currency.currencyCode,
      }),
  };

  const { mutateAsync: deleteProductConfig } = useDeleteProductConfig();

  useEffect(() => {
    if (productData) {
      setProductDataResult(productData?.result);
    }
  }, [productData]);

  const openDrawer = (method: "create" | "update", id: string | null) => {
    // setIsDrawerOpen(true);
    if (method === "create") {
      setMode({ method: "create", data: null });
      setIsDrawerOpen(true);
    } else if (method === "update") {
      const findDataById = productDataResult.find(
        (item: any) => item?.mapping_id === id,
      );
      setMode({ method: "update", data: findDataById });
      setIsDrawerOpen(true);
    }
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleItemClick = (id: string) => {
    const checkExistInTableData = productDataResult.findIndex(
      (item: any) => item?.mapping_id === id,
    );
    if (checkExistInTableData !== -1) {
      const index = selectedRow.indexOf(id);
      // If ID is not in the selectedRow array, add it
      if (index === -1) {
        setSelectedRow([...selectedRow, id]);
      } else {
        // If ID is already in the selectedRow array, remove it
        const updatedSelectedRow = [...selectedRow];
        updatedSelectedRow.splice(index, 1);
        setSelectedRow(updatedSelectedRow);
      }
    }
  };

  const onHeaderCheckboxClick = (currentState: "checked" | "unchecked") => {
    // const selectedArray: any = [];
    if (currentState === "checked") {
      const selectedRowSet = new Set(selectedRow);
      const productIds = productDataResult
        .filter((item: any) => !selectedRowSet.has(item.mapping_id))
        .map((item: any) => item.mapping_id);
      setSelectedRow(productIds);
    } else {
      setSelectedRow([]);
    }
  };

  const handleDeleteClick = async () => {
    try {
      await deleteProductConfig({ product_ids: selectedRow });
      setIsDeleteModalOpen(false);
      setSelectedRow([]);
      toast.success("Product successfully deleted");
    } catch (error) {
      toast.error("Please try again later");
    }
  };

  const componentName =
    mode?.method === "update" ? "Edit Product" : "Add Product";

  return (
    // <Box sx={{ padding: 3 }}>
    <HomeScreenLayout
      title="Product"
      rightActionComponent={
        <Stack direction={"row"} gap={"24px"}>
          {/* <SearchInput
            name="search"
            placeholder="Search"
            // value={searchQuery}
            value={""}
            // onChange={(e) => setSearchQuery(e.target.value)}
          /> */}
          <SearchInput
            name="search"
            placeholder="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <Button
            endIcon={<PlusIcon />}
            onClick={() => openDrawer("create", null)}
          >
            Product
          </Button>
        </Stack>
      }
    >
      <DrawerComponent
        {...{ isDrawerOpen, closeDrawer, componentName, mode }}
      />
      <Box sx={{ paddingX: "32px" }}>
        <DataTable
          cellBuilders={cellBuilders}
          showPagination={true}
          headers={BillingHeaders}
          data={isproductDataLoading ? [] : productDataResult || []}
          selectedItems={selectedRow}
          totalItems={productData?.totalCount || 0}
          totalPages={productData?.totalPages || 0}
          currentPageNumber={paginationAndSort.pageNo}
          onItemClick={handleItemClick} //check box
          onAllSelect={(currentState: any) => {
            onHeaderCheckboxClick(currentState);
          }}
          onSortingClick={(column: string, meta?: any) => {
            console.log({ column, meta });
            setPaginationAndSort({
              type: "setSortColumn",
              payload: column,
            });
          }}
          isDataLoading={isproductDataLoading}
          itemsPerPage={paginationAndSort.perPageCount}
          onPerPageChange={(itemsPerPage) => {
            setPaginationAndSort({
              type: "setPerPageCount",
              payload: itemsPerPage,
            });
          }}
          onPagination={(d) => {
            setPaginationAndSort({
              type: "setPageNo",
              payload: d,
            });
          }}
          multiselect={true}
          handleRowClick={(id: any) => {
            openDrawer("update", id?.mapping_id);
          }}
          actionContent={
            <Stack direction={"row"} gap={"24px"}>
              <Button
                onClick={() => setIsDeleteModalOpen(true)}
                variant="primary"
                sx={{
                  bgcolor: theme.palette.error.main,
                  "&:hover": { backgroundColor: "#A9050E" },
                }}
              >
                Delete
              </Button>
            </Stack>
          }
          // isCheckboxDisabled={(item: any) => {
          //   return false;
          // }}
        />
        {isDeleteModalOpen && (
          <DeleteConfirmationModal
            open={isDeleteModalOpen}
            onPrimaryButtonClick={handleDeleteClick}
            // onPrimaryButtonClick={() => {}}
            onSecondaryButtonClick={() => setIsDeleteModalOpen(false)}
            title="Delete Product Template"
            tagline="Are you sure you want to delete?"
            // tagline={`Are you sure you want to delete ${
            //   selectedCustomers.length
            // } ${selectedCustomers.length > 1 ? "customers" : "customer"}?`}
          />
        )}
      </Box>
    </HomeScreenLayout>
  );
};
